/* -----------------------------------------------------------------------------
 * User SM Actions
 *
 * @description - Actions of User SM. Check ./README.md for more info.
 * -----------------------------------------------------------------------------
 */

import { http } from '@/utils/http'
import uniqueHash from '@/utils/uniqueHash'

export default {

  /**
   * Check user email
   */
  async checkUserEmail ({ state }) {
    try {
      const res = (await http({
        url: 'leads/check-email',
        method: 'post',
        data: { email: state.user.email }
      })).data
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  },

  /**
   * Fetch poll questions
   */
  async fetchPollQuestions ({ commit }) {
    try {
      const res = (await http({
        url: 'leads/get-questionnaire',
        method: 'get'
      })).data
      if (res.status) {
        commit('SET_POLL_PROTOTYPE', res.data.questions)
        commit('SET_POLL_DEFAULTS', res.data.questions.length)
      }
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  },

  /**
   * Send poll answers
   */
  async sendPollAnswers ({ state, commit }) {
    try {
      const res = (await http({
        url: 'leads/set-answer',
        method: 'post',
        data: state.poll.answers
      })).data
      if (res.status) {
        commit('SET_RISK_PROFILES', res.data.profiles)
        commit('SET_PORTFOLIO_FIELD', { f: 'riskLimit', v: res.data.range_stop })
      }
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  },

  /**
   * Fetch risk points
   */
  async fetchRiskPoints ({ state, commit }) {
    try {
      const res = (await http({
        url: `leads/get-risk/${state.portfolio.deposit}`,
        method: 'get',
        params: { r: uniqueHash() }
      })).data
      if (res.status) {
        commit('SET_PORTFOLIO_FIELD', { f: 'profits', v: res.data.profits })
        commit('SET_PORTFOLIO_FIELD', { f: 'risk', v: null })
      }
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  },

  /**
   * Fetch tickers data
   */
  async fetchTickersData ({ state, commit }) {
    try {
      const res = (await http({
        url: `leads/get-portfolio/${state.portfolio.deposit}/${state.portfolio.risk.R}`,
        method: 'get',
        params: { r: uniqueHash() }
      })).data
      if (res.status) commit('SET_PORTFOLIO_FIELD', { f: 'tickers', v: res.data })
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  },

  /**
   * Fetch terms
   */
  async fetchTerms ({ commit }) {
    try {
      const res = (await http({
        url: 'leads/get-terms-of-use',
        method: 'get',
        params: { r: uniqueHash() }
      })).data
      if (res.status) commit('SET_TERMS', res.data)
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  },

  /**
   * Send SMS
   */
  async sendSMS ({ state, commit }) {
    try {
      const res = (await http({
        url: 'notifications/send-sms',
        method: 'post',
        data: { phone: `+${state.user.phone}` }
      })).data
      if (res.status) {
        commit('SET_SMS_FIELD', { f: 'smsWasSent', v: true })
        commit('SET_SMS_FIELD', { f: 'uuid', v: res.data.uuid })
      }
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  },

  /**
   * Confirm SMS
   */
  async confirmSMS ({ state, commit }) {
    try {
      const res = (await http({
        url: `notifications/confirm-sms/${state.sms.uuid}/`,
        method: 'post',
        data: { sms_code: state.sms.sms_code }
      })).data
      if (res.status) commit('SET_SMS_FIELD', { f: 'codeConfirmed', v: true })
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  },

  /**
   * User registration
   */
  async userRegistration ({ state, rootState, getters, rootGetters, commit }) {
    try {
      const res = (await http({
        url: 'leads/create-lead',
        method: 'post',
        data: {
          // Portfolio params
          money: state.portfolio.deposit,
          risk: state.portfolio.risk.R,
          // User params
          name: state.user.name,
          surname: state.user.surname,
          patronymic: state.user.patronymic,
          email: state.user.email,
          phone: `+${state.user.phone}`,
          password: state.user.password,
          agreement: state.user.agreement,
          // UTMs & hostname
          utm_tags: rootGetters['moduleSecurity/getQueryParams'],
          source_host_name: rootState.moduleSecurity.location.hostname || 'unknown'
        }
      })).data
      if (res.status) {
        commit('SET_USER_DATA_FIELD', { f: 'uuid', v: res.data.uuid })
        commit('SET_TRACKING', res.data.tracking)
        commit('SET_SMS_FIELD', { f: 'regComplete', v: true })
      }
      return res
    } catch (e) {
      return {
        status: false,
        errors: { global: [`Ошибка #${e.response.status}`] }
      }
    }
  }

  /**
   * Check delivery
   * @deprecated
   */
  // async checkDelivery ({ state, commit }) {
  //   try {
  //     const { status, data, errors } = (await http({
  //       url: `leads/check-delivery-leadr/${state.user.uuid}`,
  //       method: 'get'
  //     })).data
  //     if (!status) return { status: false, errors } // strange situation #845
  //     if (data.status) return { status }
  //     return {
  //       status: false,
  //       errors: { global: ['Повторите ваш запрос'] }
  //     }
  //   } catch (e) {
  //     return {
  //       status: false,
  //       errors: { global: [`Ошибка #${e.response.status}`] }
  //     }
  //   }
  // }

}
